import { Fingerprint } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/shadcn/index.js';
import { useLayoutDirection } from '../../../hooks/useDirection.js';
import { useAnyPermission, usePermission } from '../../../hooks/usePermission.js';
import { api } from '../../../lib/api.js';

import { AttendanceBoardView, type BranchOption } from './AttendanceBoardView.js';
import { AttendanceClockCard } from './AttendanceClockCard.js';
import { AttendanceImportPanel } from './AttendanceImportPanel.js';
import { AttendanceReportView } from './AttendanceReportView.js';
import { AttendanceSettingsPanel } from './AttendanceSettingsPanel.js';
import { HolidaysPanel } from './HolidaysPanel.js';
import { WorkSchedulesPanel } from './WorkSchedulesPanel.js';

/**
 * Attendance (الحضور والانصراف) — see docs/PRD-attendance.md.
 *
 * One page for everybody: the clock card for whoever is linked to an employee,
 * and the manager tabs for whoever may read the whole shop. A cashier with only
 * the self-service permission sees their own card and nothing else.
 */
export function AttendancePage(): JSX.Element {
  const { t } = useTranslation('hr');
  const dir = useLayoutDirection();
  const canRead = useAnyPermission(['hr.time.read', 'hr.time.approve']);
  const canManage = usePermission('hr.time.approve');
  const canSelf = useAnyPermission(['hr.self.time.entry', 'hr.self.read']);
  const canAdmin = usePermission('hr.admin');
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!canRead) return;
    // Scope-narrowed on the server: a branch manager is offered their branches only.
    void api().get<{ items: BranchOption[] }>('/warehouses')
      .then((r) => setBranches(r.data.items))
      .catch(() => setBranches([]));
  }, [canRead]);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Fingerprint className="h-6 w-6 text-brand" aria-hidden />
          {t('attendance.title')}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">{t('attendance.description')}</p>
      </header>

      {canSelf && (
        <div className="print:hidden">
          <AttendanceClockCard onChange={() => setRefreshKey((n) => n + 1)} />
        </div>
      )}

      {(canRead || canAdmin) && (
        <Tabs defaultValue={canRead ? 'day' : 'schedules'} dir={dir}>
          <TabsList className="h-10 flex-wrap print:hidden">
            {canRead && <TabsTrigger value="day">{t('attendance.tabs.day')}</TabsTrigger>}
            {canRead && <TabsTrigger value="report">{t('attendance.tabs.report')}</TabsTrigger>}
            {canManage && <TabsTrigger value="import">{t('attendance.tabs.import')}</TabsTrigger>}
            {canAdmin && <TabsTrigger value="schedules">{t('attendance.tabs.schedules')}</TabsTrigger>}
            {canAdmin && <TabsTrigger value="holidays">{t('attendance.tabs.holidays')}</TabsTrigger>}
            {canAdmin && <TabsTrigger value="settings">{t('attendance.tabs.settings')}</TabsTrigger>}
          </TabsList>
          {canRead && (
            <TabsContent value="day">
              <AttendanceBoardView canManage={canManage} branches={branches} refreshKey={refreshKey} />
            </TabsContent>
          )}
          {canRead && (
            <TabsContent value="report">
              <AttendanceReportView branches={branches} />
            </TabsContent>
          )}
          {canManage && (
            <TabsContent value="import">
              <AttendanceImportPanel onApplied={() => setRefreshKey((n) => n + 1)} />
            </TabsContent>
          )}
          {canAdmin && (
            <TabsContent value="schedules">
              <WorkSchedulesPanel />
            </TabsContent>
          )}
          {canAdmin && (
            <TabsContent value="holidays">
              <HolidaysPanel />
            </TabsContent>
          )}
          {canAdmin && (
            <TabsContent value="settings">
              <AttendanceSettingsPanel />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}
